'use client';
import { useEffect, useLayoutEffect } from 'react';
import { appConfig } from '../../utils/appConfig';
import MobileNav, { MobileNavProps } from './_MobileNav';
import NavDropdownWrapper from './_NavDropdownWrapper';
import Search from './_GlobalSearch';
import SitewideBanner, { type SitewideBannerProps } from './_SitewideBanner';
import Button from '@repo/ui/src/components/Button';
import { Anchor } from '@repo/ui/src/components/Anchor';
import CloseIcon from '@repo/ui/src/components/Icons/CloseIcon';
import HamburgerIcon from '@repo/ui/src/components/Icons/HamburgerIcon';
import Container from '@repo/ui/src/components/Container';
import { useScrollLock } from '@repo/ui/src/hooks/useScrollLock';
import FocusTrap from 'focus-trap-react';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import React, { useState } from 'react';
import { LinkProps } from '@repo/ui/src/types/link';
import { MetaProps } from '@repo/ui/src/types';
import SecondaryNav, { SecondaryNavProps } from './_SecondaryNav';
import NavDropdown, { NavDropdownProps } from './_NavDropdown';
import DynamicComponent from '@repo/ui/src/components/DynamicComponent';
import SearchIcon from '@repo/ui/src/components/Icons/SearchIcon';
import { navigationClicked } from '@repo/ui/src/services/analytics';
import { Region } from '@repo/ui/src/types/LanguageSelector';
import { classNames } from '@repo/ui/src/utils/classNames';
import { VipProvider } from './_VipContext';

export interface NavItemProps {
  _meta: MetaProps;
  title: string;
  titleAsLink?: LinkProps;
  content: NavDropdownProps | SecondaryNavProps;
}

export interface NavigationProps {
  searchLink: LinkProps;
  navItems: Array<NavItemProps>;
  sitewideBanner: SitewideBannerProps;
  regions: Array<Region>;
  NavbarBrand?: React.ComponentType;
  NavbarShoppingCart?: React.ComponentType;
  UserAvatar?: React.ComponentType<{
    className?: string;
    loggedInLabel?: string;
    loggedOutText?: string;
  }>;
  SocialLinks?: React.ComponentType<{ socialLinks: any }>;
  account?: MobileNavProps['account'];
  useKeyboardNavigation?: (options: any) => (e: React.KeyboardEvent) => void;
  useOnRouteChange?: (callback: () => void) => void;
  useCustomer?: () => { data: any };
  useLogout?: () => { trigger: () => void };
  getTZDate?: (dateString: string) => Date;
  vip?: boolean;
}

const componentTypes = {
  'secondaryNav.json': SecondaryNav,
  'navDropdown.json': NavDropdown,
};

export const isDropdown = (item: NavItemProps) =>
  item.content?._meta?.schema?.endsWith('navDropdown.json');

const NavigationAnimationContext = React.createContext({
  setHeight: noop,
  setOffset: noop,
});

export const useNavigationContext = () => React.useContext(NavigationAnimationContext);

const Navigation = ({
  navItems,
  searchLink,
  sitewideBanner,
  regions,
  NavbarBrand,
  NavbarShoppingCart,
  UserAvatar,
  SocialLinks,
  account,
  useKeyboardNavigation = () => noop,
  useOnRouteChange = noop,
  useCustomer,
  useLogout,
  getTZDate,
  vip,
}: NavigationProps) => {
  const { lock, unlock } = useScrollLock();
  const sitewideBannerRef = React.useRef<HTMLDivElement>(null);
  const headerRef = React.useRef<HTMLElement>(null);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const [dropdownHeight, setDropdownHeight] = useState(0);
  const [dropdownOffset, setDropdownOffset] = useState(0);

  useLayoutEffect(() => {
    const getOffsetHeight = () => {
      const headerHeight = headerRef.current?.getBoundingClientRect().height ?? 0;
      const bannerHeight = sitewideBannerRef.current?.getBoundingClientRect().height ?? 0;
      setOffset(headerHeight + bannerHeight);
    };

    getOffsetHeight();

    // In case dynamic content loads later
    const timeout = setTimeout(getOffsetHeight, 200);
    const handleResize = debounce(getOffsetHeight, 200);

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (openDropdown != null) {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && openDropdown != null) {
          (
            headerRef.current?.querySelector(
              `[data-testid="main-nav-${openDropdown}"]`,
            ) as HTMLElement
          )?.focus();
          setOpenDropdown(null);
        }
      };

      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [openDropdown]);

  useOnRouteChange(() => {
    setMobileDrawerOpen(false);
    setOpenDropdown(null);
  });

  useEffect(() => {
    if (mobileDrawerOpen || openDropdown != null) {
      lock();
    } else {
      unlock();
    }
  }, [mobileDrawerOpen, openDropdown, lock, unlock]);

  const handleKeyDown: (e: React.KeyboardEvent) => void = useKeyboardNavigation({
    items: navItems,
    onChange: (index: number) => {
      setOpenDropdown(index);
      (
        headerRef.current?.querySelector(`[data-testid="main-nav-${index}"]`) as HTMLElement
      )?.focus();
    },
  });

  if (!navItems || navItems.length === 0) return null;

  return (
    <VipProvider vip={vip}>
      <Button
        as={Anchor}
        link="#main-content"
        size="small"
        className={classNames(
          'z-[1100] absolute -top-20 transition-top ease-in-out duration-[250] focus:top-1 left-1',
        )}
      >
        {'Skip to Main Content'}
      </Button>
      <div ref={sitewideBannerRef}>
        {sitewideBanner && (
          <SitewideBanner {...sitewideBanner} regions={regions} getTZDate={getTZDate} />
        )}
      </div>
      <FocusTrap active={mobileDrawerOpen}>
        <header
          className={classNames(
            'sticky top-0 z-[1001] block after:bg-black after:fixed after:inset-0 after:transition-opacity after:duration-200 after:-z-30 after:pointer-events-none',
            openDropdown != null ? 'after:opacity-50' : 'after:opacity-0',
          )}
          ref={headerRef}
          data-testid="overlay-header"
        >
          <NavigationAnimationContext.Provider
            value={{ setHeight: setDropdownHeight, setOffset: setDropdownOffset }}
          >
            <div
              onMouseLeave={() => setOpenDropdown(null)}
              className={classNames(
                'bg-neutral-700 !text-white flex flex-col',
                'after:inset-x-0 after:top-full after:-z-20 after:absolute after:block after:bg-warm-100 after:h-[calc(var(--dropdown-height,0)+var(--dropdown-offset,0))] after:max-h-[calc(85vh-var(--tra-nav-height))] after:[transition:height_500ms,opacity_300ms] after:duration-500 after:pointer-events-none',
                openDropdown === null
                  ? 'after:opacity-0 after:[transition:height_500ms_500ms,opacity_300ms]'
                  : 'after:opacity-100',
              )}
              aria-label="main"
              style={{
                // @ts-expect-error unknown style
                '--dropdown-height': `${dropdownHeight}px`,
                '--dropdown-offset': `${dropdownOffset}px`,
              }}
            >
              <Container className="min-h-navHeight w-full max-h-[500px] items-center grid grid-flow-col justify-between md:grid-cols-[1fr_auto_1fr] gap-5 self-start">
                {NavbarBrand && <NavbarBrand />}
                <nav aria-label="Main Menu" data-testid="desktop-nav" className="hidden sm:block">
                  <ul className="items-center justify-center pl-0 list-none sm:flex gap-x-10">
                    {navItems.map((item, i) => {
                      const hasContent = !!item.content;
                      const hasLink = !!item.titleAsLink;

                      if (!hasContent && !hasLink) return null;

                      const isOpen = openDropdown === i;
                      return (
                        <li
                          key={i}
                          onMouseEnter={() =>
                            hasContent ? setOpenDropdown(i) : setOpenDropdown(null)
                          }
                        >
                          {item.titleAsLink && !hasContent ? (
                            <Anchor
                              {...item.titleAsLink}
                              data-testid={`main-nav-${i}`}
                              onClick={() => {
                                navigationClicked({
                                  nav_type: 'Top Navigation',
                                  clicked_text: navItems[i].title ?? '',
                                  primary_header: navItems[i].title ?? '',
                                });
                              }}
                              className={classNames(
                                'tra-text-web-sub-2 relative after:transition-all after:duration-300 after:opacity-100 after:absolute after:inset-x-0 after:-bottom-2 after:w-full after:h-[4px] after:bg-brand-300 after:scale-0 after:origin-center hover:after:scale-100',
                                appConfig.isMeater &&
                                  'tra-text-web-utl-button-m after:h-[2px] after:-bottom-3',
                              )}
                            >
                              {item.title}
                            </Anchor>
                          ) : (
                            <button
                              data-testid={`main-nav-${i}`}
                              data-index={i}
                              onKeyDown={handleKeyDown}
                              onFocus={() => setOpenDropdown(i)}
                              onClick={() => {
                                navigationClicked({
                                  nav_type: 'Top Navigation',
                                  clicked_text: navItems[i].title ?? '',
                                  primary_header: navItems[i].title ?? '',
                                });
                              }}
                              className={classNames(
                                'tra-text-web-sub-2 relative after:transition-all after:duration-300 after:opacity-100 after:absolute after:inset-x-0 after:-bottom-2 after:w-full after:h-[4px] after:bg-brand-300 after:scale-0 after:origin-center hover:after:scale-100',
                                isOpen && 'after:scale-100',
                                appConfig.isMeater &&
                                  'tra-text-web-utl-button-m after:h-[2px] after:-bottom-3',
                              )}
                              aria-expanded={isOpen}
                              aria-controls={`nav-dropdown-${i}`}
                              aria-haspopup="true"
                            >
                              {item.title}
                            </button>
                          )}
                          {hasContent && (
                            <NavDropdownWrapper
                              id={`nav-dropdown-${i}`}
                              open={isOpen}
                              isDropdown={isDropdown(item)}
                              onTransitionEnd={() => {
                                if (openDropdown === null) {
                                  setDropdownHeight(0);
                                  setDropdownOffset(0);
                                }
                              }}
                            >
                              <DynamicComponent
                                {...item.content}
                                _componentTypes={componentTypes}
                                searchLink={searchLink}
                                open={openDropdown === i && hasContent}
                                primaryHeader={item?.title}
                                SocialLinks={SocialLinks}
                              />
                            </NavDropdownWrapper>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </nav>
                <ul className="flex flex-row items-center justify-end pl-0 -mr-3 list-none md:gap-4 sm:-mr-1">
                  {searchLink?.link && (
                    <>
                      <Anchor className="p-3 lg:hidden" aria-label="Search" link={searchLink.link}>
                        <SearchIcon className="w-6" />
                      </Anchor>
                      <li>
                        <Search placeholder={searchLink.displayName} link={searchLink.link} />
                      </li>
                    </>
                  )}
                  {UserAvatar && (
                    <li>
                      <UserAvatar
                        className="hidden md:inline-block"
                        loggedInLabel={account?.loggedInLabel}
                        loggedOutText={account?.loggedOutText}
                      />
                    </li>
                  )}
                  {NavbarShoppingCart && (
                    <li>
                      <NavbarShoppingCart />
                    </li>
                  )}
                  <button
                    className="p-3 sm:hidden"
                    aria-label={mobileDrawerOpen ? 'close navigation' : 'open navigation'}
                    onClick={() => {
                      setMobileDrawerOpen((currentlyOpen) => !currentlyOpen);
                    }}
                  >
                    {mobileDrawerOpen ? (
                      <CloseIcon className="w-6" />
                    ) : (
                      <HamburgerIcon className="w-6" />
                    )}
                  </button>
                </ul>
              </Container>
              <MobileNav
                open={mobileDrawerOpen}
                offset={offset}
                navItems={navItems}
                regions={regions}
                searchLink={searchLink}
                account={account}
                dealerLocator={sitewideBanner?.dealerLocatorLink}
                useCustomer={useCustomer}
                useLogout={useLogout}
              />
            </div>
          </NavigationAnimationContext.Provider>
        </header>
      </FocusTrap>
    </VipProvider>
  );
};

// Export individual components for use in CMS
export { default as NavDropdown } from './_NavDropdown';
export { default as SecondaryNav } from './_SecondaryNav';
export { default as LinkGroup } from './_LinkGroup';
export { default as LinkList } from './_LinkList';
export { default as ProductList } from './_ProductList';

// Export types
export type { NavDropdownProps } from './_NavDropdown';
export type { SecondaryNavProps } from './_SecondaryNav';
export type { LinkGroupProps, LinkWithIconProps } from './_LinkGroup';
export type { LinkListProps } from './_LinkList';

export default Navigation;
